import { useContext, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { LanguageContext } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#121212] border border-white/10 rounded-xl px-5 py-4 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#00F0FF]/60 focus:shadow-[0_0_25px_rgba(0,240,255,0.08)] transition-[border-color,box-shadow] duration-300";

export default function Contact() {
  const { t, lang } = useContext(LanguageContext);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      setStatus("sent");
      toast.success(t.contact.successTitle, { description: t.contact.successMsg });
    } catch (err) {
      setStatus("idle");
      toast.error(t.contact.error);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 md:py-40">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#00F0FF] mb-6">{t.contact.label}</p>
          <h2 className="font-display font-black tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mb-8">
            {t.contact.heading}
          </h2>
          <p className="text-base md:text-lg font-light text-zinc-400 leading-relaxed max-w-md">{t.contact.sub}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
        >
          {status === "sent" ? (
            <div data-testid="contact-success-message" className="h-full min-h-[380px] rounded-2xl border border-[#00F0FF]/30 bg-[#121212] flex flex-col items-center justify-center gap-4 p-10 text-center shadow-[0_0_60px_rgba(0,240,255,0.07)]">
              <CheckCircle2 size={44} className="text-[#00F0FF]" />
              <h3 className="font-display font-bold text-2xl text-white">{t.contact.successTitle}</h3>
              <p className="text-sm font-light text-zinc-400 max-w-xs">{t.contact.successMsg}</p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder={t.contact.namePlaceholder} className={inputCls} />
                <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={t.contact.emailPlaceholder} className={inputCls} />
              </div>
              <input data-testid="contact-subject-input" required value={form.subject} onChange={set("subject")} placeholder={t.contact.subjectPlaceholder} className={inputCls} />
              <textarea data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} placeholder={t.contact.messagePlaceholder} className={`${inputCls} resize-none`} />
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={status === "sending"}
                className="group mt-2 flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-[#00F0FF] text-black text-sm font-semibold hover:shadow-[0_0_50px_rgba(0,240,255,0.45)] disabled:opacity-60 transition-shadow duration-300"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t.contact.sending}
                  </>
                ) : (
                  <>
                    {t.contact.send}
                    <Send size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 rtl:-scale-x-100" />
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
